/* eslint-disable react/jsx-no-target-blank */
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Flex, Text, Heading, Spinner } from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import { getLastFinancialYear } from "@syla/shared/helpers/financialYear";
import {
  satisfiesEntityType,
  satisfiesActivityType,
  satisfiesTransactionCount,
  satisfiesAllRequirements,
} from "@syla/shared/types/helpers/permissions";
import {
  AccountType,
  AccountSubType,
  ActivityTypeName,
} from "@syla/shared/types/models/AccountBase";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  RenewalAction,
  BillingStatus,
  Lifecycle,
} from "@syla/shared/types/subscriptions/subscriptionState";
import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { PageContainer } from "../components/atoms/Containers";
import { PageHeading } from "../components/atoms/Headings";
import { PlanCard, Feature } from "../components/molecules/PlanCard";
import { getFullPageUrl } from "../helper/getFullPageUrl";
import {
  canPurchaseSubscription,
  canManageExistingSubscription,
} from "../helper/permissions";
import { useReportError } from "../helpers/useReportError";
import { useCurrentAccountStore } from "../store/currentAccountStore";
import { Plan } from "../types/plans/plan";
import { Subscription } from "../types/plans/subscription";
import { stripePurchaseOrUpgrade } from "./stripePurchaseOrUpgrade";
import { useGetAccountPlanProps } from "./useGetAccountPlanProps";

export type PlansNavState = {
  referringUrl: string;
  selectedFinancialYear?: string;
};

function getUnsuitablePlanMessage({
  plan,
  account,
  transactionCount,
}: {
  account: { accountType: AccountType; accountSubType: AccountSubType };
  plan: Plan;
  transactionCount: number;
}) {
  if (!satisfiesEntityType({ account, plan })) {
    const article = account.accountType === AccountType.SMSF ? "an" : "a";
    return `This plan doesn't support ${article} ${account.accountType} account. Update your Account Type in Settings or choose a compatible plan.`;
  }

  if (!satisfiesActivityType({ account, plan })) {
    return `This plan doesn't support ${ActivityTypeName[AccountSubType.Trader]} activity. Update your Activity Type in Settings or choose a compatible plan.`;
  }

  if (!satisfiesTransactionCount({ plan, transactionCount })) {
    return "The number of transactions in your account exceeds the limit on this plan.";
  }

  throw new Error(`Invalid case`);
}

export const Plans = () => {
  const { state } = useLocation<PlansNavState | undefined>();
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const errorToast = useReportError();

  const selectedFinancialYear =
    state?.selectedFinancialYear ?? getLastFinancialYear();

  const {
    availablePlans,
    currentSubscription,
    currentPlan,
    recommendedPlan,
    transactionCount,
    isLoading: isAccountPlansLoading,
    account,
  } = useGetAccountPlanProps();

  const hasPurchasePermission =
    canPurchaseSubscription({
      account,
      subscriptions: currentSubscription ? [currentSubscription] : [],
    }) ||
    canManageExistingSubscription({
      subscriptions: currentSubscription ? [currentSubscription] : [],
    });

  const actionNeeded =
    currentSubscription &&
    getSubscriptionState(currentSubscription).billing === BillingStatus.Unpaid;

  const openCheckoutSession = useCallback(
    async (plan: Pick<Plan, "_id">, subscription?: Subscription) => {
      try {
        setRedirecting(true);
        await stripePurchaseOrUpgrade({
          accountId,
          successUrl: state?.referringUrl ?? getFullPageUrl(location),
          cancelUrl: getFullPageUrl(location),
          financialYear: selectedFinancialYear,
          currentSubscription: subscription,
          plan,
        });
      } catch (error) {
        errorToast(error);
        return;
      } finally {
        setRedirecting(false);
      }
    },
    [
      accountId,
      currentSubscription?.plan,
      errorToast,
      selectedFinancialYear,
      state?.referringUrl,
    ],
  );

  if (isAccountPlansLoading) {
    return (
      <PageContainer>
        <Flex justifyContent="center">
          <Spinner
            color="primary.500"
            emptyColor="primary.200"
            my="50px"
            thickness="5px"
            size="xl"
          />
        </Flex>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading size="2xl" textAlign="center" mb={8}>
        Tax Plans
      </PageHeading>

      <Flex justify="center" w="full">
        <TransactionCountDisplay transactionCount={transactionCount} />
      </Flex>

      <Flex w="full" justifyContent="center" flexWrap="wrap" mt="20px">
        {availablePlans.map((plan) => {
          const isCurrentPlan = currentPlan?._id === plan._id;

          let expiredDate: string | undefined = undefined;
          let cancelDate: string | undefined = undefined;
          let purchased = false;
          let actionRequired = false;

          if (isCurrentPlan && currentSubscription) {
            const subscriptionState = getSubscriptionState(currentSubscription);

            const endDateFormatted =
              subscriptionState.periodEnd.toLocaleDateString();

            if (subscriptionState.lifecycle === Lifecycle.Terminated) {
              expiredDate = endDateFormatted;
            } else {
              purchased = true;
            }

            if (subscriptionState.renewal === RenewalAction.Cancel) {
              cancelDate = endDateFormatted;
            }

            if (subscriptionState.billing === BillingStatus.Unpaid) {
              actionRequired = true;
            }
          }

          const recommended = plan._id === recommendedPlan?._id;

          const isPlanSuitable = satisfiesAllRequirements({
            account,
            plan,
            transactionCount,
          });

          return (
            <PlanCard
              key={plan._id}
              price={plan.price}
              heading={plan.name}
              description={plan.description}
              featureList={getFeatureList(plan)}
              action={() => openCheckoutSession(plan, currentSubscription)}
              purchasable={
                hasPurchasePermission &&
                ((actionNeeded && isCurrentPlan) ||
                  (!actionNeeded && isPlanSuitable))
              }
              canManage={hasPurchasePermission}
              notPurchasableReason={getNotPurchasableReason(
                hasPurchasePermission,
                isPlanSuitable,
                account,
                transactionCount,
                plan,
              )}
              headerText={
                expiredDate ? `Expired ${expiredDate}`
                : cancelDate ?
                  `Expires ${cancelDate}`
                : actionRequired ?
                  "Action Required"
                : purchased ?
                  "Subscribed"
                : recommended ?
                  "Recommended"
                : undefined
              }
              status={
                expiredDate ? "expired"
                : cancelDate ?
                  "cancelled"
                : actionRequired ?
                  "actionRequired"
                : purchased ?
                  "purchased"
                : recommended ?
                  "recommended"
                : "normal"
              }
              purchased={isCurrentPlan}
              purchasedPrice={currentPlan?.price}
              upgrading={actionNeeded}
              previousPrice={0}
              isBusy={redirecting}
            />
          );
        })}
      </Flex>
    </PageContainer>
  );
};

const TransactionCountDisplay = ({
  transactionCount,
}: {
  transactionCount: number | undefined;
}) => {
  const getTransactionDisplay = () => {
    if (transactionCount === undefined) {
      return <Spinner size="xs" />;
    }
    return Decimal.from(transactionCount.toString()).format({
      separateThousands: true,
    }).text;
  };

  return (
    <Flex align="center" gap={2}>
      <InfoOutlineIcon />
      <Text fontSize="sm" color="gray.600" fontWeight="medium">
        You currently have{" "}
        <strong>{getTransactionDisplay()} transactions</strong> on your account.
      </Text>
    </Flex>
  );
};

function getFeatureList(plan: Plan): Feature[] {
  const transactionCap = Decimal.from(
    plan.features.transactionLimit.toString(),
  ).format({
    separateThousands: true,
  }).text;

  return [
    {
      text: `${transactionCap} Transactions`,
      included: true,
    },
    {
      text: "All Financial Years",
      included: true,
    },
    {
      text: "Crypto Tax Report",
      included: true,
    },
    {
      text: "SMSF, Company and Trust",
      included: plan.features.entities,
    },
    {
      text: "BGL Sync",
      included: plan.features.premiumIntegrations,
    },
    {
      text: "Assurance Reports",
      included: plan.features.assuranceReports,
    },
    {
      text: "LTFO Tax Saving",
      included: plan.features.taxOptimisation,
    },
    {
      text: "Trading Stock",
      included: plan.features.tradingStock,
    },
  ];
}

function getNotPurchasableReason(
  canPurchase: boolean,
  isPlanSuitable: boolean,
  account,
  transactionCount,
  plan,
) {
  if (!canPurchase) {
    return "Please contact the billing owner for this account to purchase a plan";
  }

  if (!isPlanSuitable && !!account && transactionCount !== undefined) {
    return getUnsuitablePlanMessage({
      account,
      plan,
      transactionCount,
    });
  }

  if (transactionCount === undefined) {
    return "Please wait for your tax calculations to complete";
  }
}
